import React, { useState, useMemo, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { dummySupportTickets } from "../../../data/dummyData";
import "../Home/Homepage.css";
import "./SupportManagementPage.css";

const MAX_LENGTH = 50;

const formatDateTime = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso || "N/A";
  }
};

const truncateText = (text, max) => {
  if (!text) return "-----";
  if (text.length <= max) return text;
  return text.substring(0, max) + "...";
};

const SupportManagementPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tickets, setTickets] = useState(dummySupportTickets);
  const [modalContent, setModalContent] = useState({ type: "", text: "", isOpen: false });

  useEffect(() => {
    const u = location.state?.updatedTicket;
    if (u?._id) {
      setTickets((prev) => prev.map((t) => (t._id === u._id ? { ...t, status: u.status } : t)));
      navigate(".", { replace: true, state: {} });
    }
  }, [location.state, navigate]);

  const counts = useMemo(() => ({
    total: tickets.length,
    open: tickets.filter((t) => (t.status || "").toLowerCase() === "open").length,
    resolved: tickets.filter((t) => (t.status || "").toLowerCase() === "resolved").length,
    closed: tickets.filter((t) => (t.status || "").toLowerCase() === "closed").length,
  }), [tickets]);

  const handleStatusChange = (id, newStatus) => {
    setTickets((prev) => prev.map((t) => (t._id === id ? { ...t, status: newStatus, seen: 1 } : t)));
  };

  const handleReadMore = (type, fullText) => {
    setModalContent({ type: type === "subject" ? "Subject" : "Description", text: fullText || "-----", isOpen: true });
  };

  const handleChat = (row) => {
    navigate("/dashboard/support-message", { state: { ticket: row } });
  };

  const exportCSV = () => {
    const headers = ["Date", "User Id", "Email Id", "Subject", "Status", "Description"];
    const rows = tickets.map((t) => [
      formatDateTime(t.createdAt),
      t.userId || "",
      t.emailId || "",
      t.subject || "",
      t.status || "",
      t.description || "",
    ]);
    const csv = [headers.join(","), ...rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "support_tickets.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  return (
    <main className="dashboard-main dashboard-subpage support-management-page">
      <section className="dashboard-hero support-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Support</span>
          </p>
          <h1 className="dashboard-hero__title">Support Management</h1>
          <p className="dashboard-hero__subtitle">Manage customer support tickets and queries</p>
        </div>
        <div className="support-hero__icon">
          <i className="fas fa-headset" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="support-stats">
          <div className="support-stat-card">
            <span className="support-stat-card__value">{counts.total}</span>
            <span className="support-stat-card__label">Total Tickets</span>
          </div>
          <div className="support-stat-card support-stat-card--open">
            <span className="support-stat-card__value">{counts.open}</span>
            <span className="support-stat-card__label">Open</span>
          </div>
          <div className="support-stat-card support-stat-card--resolved">
            <span className="support-stat-card__value">{counts.resolved}</span>
            <span className="support-stat-card__label">Resolved</span>
          </div>
          <div className="support-stat-card support-stat-card--closed">
            <span className="support-stat-card__value">{counts.closed}</span>
            <span className="support-stat-card__label">Closed</span>
          </div>
        </div>

        <section className="support-list-section">
          <div className="support-list-header">
            <h2 className="support-list__title"><i className="fas fa-list-alt" /> Issue List ({tickets.length})</h2>
            {tickets.length > 0 && (
              <div className="support-export-wrap">
                <button type="button" className="support-btn-export" onClick={exportCSV}>
                  <i className="fas fa-download" /> Export
                </button>
              </div>
            )}
          </div>
          <div className="support-table-wrap">
            {tickets.length === 0 ? (
              <div className="support-empty"><i className="fas fa-headset" /><p>No support tickets found</p></div>
            ) : (
              <table className="support-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Issue Image</th>
                    <th>User Id</th>
                    <th>Email Id</th>
                    <th>Subject</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((row) => {
                    const subj = row.subject || "-----";
                    const desc = row.description || "-----";
                    const subjLong = subj.length > MAX_LENGTH;
                    const descLong = desc.length > MAX_LENGTH;
                    return (
                      <tr key={row._id}>
                        <td>{formatDateTime(row.createdAt)}</td>
                        <td>{row.issueImage ? <a href={row.issueImage} target="_blank" rel="noreferrer" className="support-img-link">View</a> : <span className="support-no-image">No Image</span>}</td>
                        <td><span className={row.seen === 1 ? "support-seen" : "support-unseen"}>{row.userId || "-----"}</span></td>
                        <td><span className={row.seen === 1 ? "support-seen" : "support-unseen"}>{row.emailId || "-----"}</span></td>
                        <td className="support-text-cell">
                          <span className={row.seen === 1 ? "support-seen" : "support-unseen"}>{truncateText(subj, MAX_LENGTH)}</span>
                          {subjLong && <button type="button" className="support-read-more" onClick={(e) => { e.stopPropagation(); handleReadMore("subject", subj); }}>Read More</button>}
                        </td>
                        <td className="support-text-cell">
                          <span className={row.seen === 1 ? "support-seen" : "support-unseen"}>{truncateText(desc, MAX_LENGTH)}</span>
                          {descLong && <button type="button" className="support-read-more" onClick={(e) => { e.stopPropagation(); handleReadMore("description", desc); }}>Read More</button>}
                        </td>
                        <td>
                          <span className={`support-status-badge support-status-badge--${(row.status || "").toLowerCase()}`}>
                            {row.status}{row.seen === 1 && <i className="fas fa-circle support-status-dot" />}
                          </span>
                        </td>
                        <td>
                          <div className="support-actions">
                            {(row.status || "").toLowerCase() === "open" ? (
                              <>
                                <button type="button" className="support-action-btn support-action-btn--close" onClick={() => handleStatusChange(row._id, "Closed")}><i className="fas fa-times-circle" /> Close</button>
                                <button type="button" className="support-action-btn support-action-btn--resolve" onClick={() => handleStatusChange(row._id, "Resolved")}><i className="fas fa-check-circle" /> Resolve</button>
                                <button type="button" className="support-action-btn support-action-btn--chat" onClick={() => handleChat(row)}><i className="fas fa-comments" /> Chat</button>
                              </>
                            ) : (
                              <div className="support-actions-closed">
                                <span className={`support-actions-status-text support-actions-status-text--${(row.status || "").toLowerCase()}`}>{row.status}</span>
                                <button type="button" className="support-action-btn support-action-btn--chat" onClick={() => handleChat(row)}><i className="fas fa-comments" /> Chat</button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>

      {modalContent.isOpen && (
        <div className="support-modal-overlay" onClick={() => setModalContent((m) => ({ ...m, isOpen: false }))}>
          <div className="support-modal" onClick={(e) => e.stopPropagation()}>
            <div className="support-modal-header">
              <h3 className="support-modal-title">Full {modalContent.type}</h3>
              <button type="button" className="support-modal-close" onClick={() => setModalContent((m) => ({ ...m, isOpen: false }))}><i className="fas fa-times" /></button>
            </div>
            <div className="support-modal-body"><p className="support-modal-text">{modalContent.text}</p></div>
            <div className="support-modal-footer">
              <button type="button" className="support-modal-btn-close" onClick={() => setModalContent((m) => ({ ...m, isOpen: false }))}>Close</button>
            </div>
          </div>
        </div>
      )}

    </main>
  );
};

export default SupportManagementPage;
